import * as React from 'react';

import {
  Checkbox,
  CheckboxVisibility,
  DetailsList,
  IColumn,
  Link,
  MessageBar,
  MessageBarType,
  Persona,
  PersonaSize,
  SearchBox,
  Spinner,
  SpinnerSize,
  Toggle,
} from '@fluentui/react';

import { PageHeader } from '../core/PageHeader';
import { ROUTES } from '../../config/routes';
import { SearchProvider } from '../../redux/providers/types';
import { InfoMessage } from '../core/InfoMessage';
import { formatPhoneNumber } from '../../util/PhoneNumber';
import { NavigateFunction } from 'react-router-dom';

export interface OwnProps {
  navigate: NavigateFunction;
}

export interface StateProps {
  isSearching: boolean;
  providers?: SearchProvider[];
}

export interface DispatchProps {
  actions: {
    providerSearchChanged: (newValue: string) => void;
  };
}

type ProvidersProps = OwnProps & StateProps & DispatchProps;

export interface ProvidersState {
  columns: IColumn[];
  searchText: string;
  showActiveOnly: boolean;
  showPDOnly: boolean;
}

export class Providers extends React.Component<ProvidersProps, ProvidersState> {
  constructor(props: ProvidersProps) {
    super(props);

    this.state = {
      columns: this.buildColumns(),
      searchText: '',
      showActiveOnly: true,
      showPDOnly: false,
    };
  }

  public render(): JSX.Element {
    const providers = (this.props.providers || []).filter((p) => {
      if (this.state.showActiveOnly && !p.active) {
        return false;
      }
      if (this.state.showPDOnly && (!p.pdMarkets || p.pdMarkets.length === 0)) {
        return false;
      }
      return true;
    });

    return (
      <div>
        <PageHeader
          title="Providers"
          actions={{
            items: [
              {
                key: 'addProvider',
                text: 'Create provider',
                iconProps: { iconName: 'Add' },
                onClick: () => this.props.navigate(ROUTES.providerCreate),
              },
            ],
          }}
        />
        <div className="flex">
          <SearchBox className="flex-grow" onChange={this.onSearchChange} data-tid="inp_search" />
          <div style={{ marginLeft: 10 }}>
            <Toggle
              className="flex flex-center"
              style={{ margin: 0 }}
              label="Only PD&nbsp;"
              checked={this.state.showPDOnly}
              onChange={(e, checked) => checked !== undefined && this.setState({ showPDOnly: checked })}
              data-tid="btn_onlyPD"
            />
          </div>
          <div style={{ marginLeft: 10 }}>
            <Toggle
              className="flex flex-center"
              style={{ margin: 0 }}
              label="Only active&nbsp;"
              checked={this.state.showActiveOnly}
              onChange={(e, checked) => checked !== undefined && this.setState({ showActiveOnly: checked })}
              data-tid="btn_onlyActive"
            />
          </div>
        </div>
        <DetailsList checkboxVisibility={CheckboxVisibility.hidden} items={providers} setKey="set" columns={this.state.columns} onItemInvoked={this.onItemInvoked} />
        {this.renderStatus()}
      </div>
    );
  }

  private renderStatus(): JSX.Element | null {
    if (this.props.isSearching) {
      return <Spinner style={{ marginTop: 20 }} size={SpinnerSize.large} label="Searching for you..." />;
    }

    if (this.state.searchText && this.props.providers && this.props.providers.length === 0) {
      return (
        <MessageBar messageBarType={MessageBarType.info} isMultiline={true}>
          There are no results, please refine your search.
        </MessageBar>
      );
    } else if (!this.props.providers || this.props.providers.length === 0) {
      return (
        <InfoMessage>
          <div>Search for any provider by first name, last name, or email</div>
        </InfoMessage>
      );
    }
    return null;
  }

  private onSearchChange = (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
    this.setState({ searchText: newValue! });
    this.props.actions.providerSearchChanged(newValue!);
  };

  private onItemInvoked = (item: SearchProvider) => {
    this.props.navigate(`${ROUTES.providers}/${item.providerId}`);
  };

  private buildColumns(): IColumn[] {
    return [
      {
        key: 'provider',
        name: 'Provider',
        minWidth: 80,
        maxWidth: 320,
        onRender: (item: SearchProvider) => {
          const name = item.firstName + ' ' + item.lastName;
          const onRenderPrimaryText = () => {
            return <Link onClick={() => this.props.navigate(`${ROUTES.providers}/${item.providerId}`)}>{item.fullName}</Link>;
          };
          return <Persona size={PersonaSize.size24} text={name} onRenderPrimaryText={onRenderPrimaryText} imageUrl={item!.avatarUrl} />;
        },
      },
      {
        key: 'email',
        name: 'Email',
        fieldName: 'email',
        minWidth: 120,
        maxWidth: 240,
      },
      {
        key: 'phone',
        name: 'Phone',
        fieldName: 'phone',
        minWidth: 120,
        maxWidth: 120,
        onRender: (item: SearchProvider) => {
          return formatPhoneNumber(item.phone);
        },
      },
      {
        key: 'markets',
        name: 'Markets',
        fieldName: 'serviceMarkets',
        minWidth: 120,
        maxWidth: 160,
        isResizable: true,
        onRender: (item: SearchProvider) => {
          return item.serviceMarkets.join(', ') || '-';
        },
      },
      {
        key: 'pdMarkets',
        name: 'Preferred Markets',
        fieldName: 'pdMarkets',
        minWidth: 120,
        maxWidth: 160,
        isResizable: true,
        onRender: (item: SearchProvider) => {
          return item.pdMarkets.join(', ') || '-';
        },
      },
      {
        key: 'active',
        name: 'Active',
        fieldName: 'active',
        minWidth: 60,
        maxWidth: 60,
        onRender: (item: SearchProvider) => {
          return <Checkbox disabled={true} checked={item.active} />;
        },
      },
    ];
  }
}
